<template>
  <div class="provisioned-repair">
    <span>
      Your organization seems to be slightly damaged.<br />Please click repair
      to fix everything.
    </span>
    <BaseButton
      kind="main"
      type="primary"
      :text="repairing ? 'Repairing…' : 'Repair'"
      :state="repairing ? 'loading' : 'default'"
      @click="$emit('repair')"
    />
    <span v-if="failed" class="error">
      The repair failed to fix your account.<br />You can fix it manually by
      <RouterLink v-if="typesRoute" class="link" :to="typesRoute"
        >editing your Task type</RouterLink
      ><template v-else>editing your Task type</template>
      and pointing the Project relation to another type named Project whose
      schema contains a Title and a Notes property.
    </span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { RouterLink, type RouteLocationRaw } from "vue-router";

import BaseButton from "@/components/base/BaseButton.vue";
import * as inject from "@/components/inject";
import { getOrganization } from "@/composables/useProfile";
import { RouteNames } from "@/routes/types";

/**
 * Empty state shown by the Tasks/Pages/Projects screens when the
 * organization's provisioned types need a repair (see
 * `useProvisionedRepair`). The host sizes it (e.g. `flex: 1` or
 * `height: 100%`) so it fills the screen; the content centers itself.
 */
export default defineComponent({
  name: "ProvisionedRepair",
  components: { BaseButton, RouterLink },
  props: {
    /** The organization whose types broke; the manual-fix message links to
     * its Types settings screen. */
    orgId: { type: String, required: true },
    /** Renders the Repair button in its loading state. */
    repairing: { type: Boolean, default: false },
    /** The last repair attempt left something broken: explain the manual
     * fix underneath the button (see `repairFailed` in
     * `useProvisionedRepair`). */
    failed: { type: Boolean, default: false },
  },
  emits: ["repair"],
  setup(props) {
    const profile = inject.profile();

    // The Types settings screen of the organization. The route is
    // workspace-scoped; the organization's first workspace is a fine entry
    // point (in practice a failed repair only happens in the personal
    // organization, which has a single workspace).
    const typesRoute = computed<RouteLocationRaw | undefined>(() => {
      const workspaceId = getOrganization(profile.value, props.orgId)
        ?.workspaces[0]?.id;
      if (!workspaceId) return undefined;
      return {
        name: RouteNames.SettingsTypes,
        params: { orgId: props.orgId, workspaceId },
      };
    });

    return { typesRoute };
  },
});
</script>

<style scoped lang="scss">
.provisioned-repair {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;

  span {
    text-align: center;
    max-width: 480px;
    @extend %font-page-regular;
    @include themed() {
      color: t($grey-50);
    }
  }

  .error {
    @extend %font-caption-regular;
    @include themed() {
      color: t($secondary-50);
    }

    .link {
      // Same blue as the "New" badge ($tertiary-50) instead of the
      // browser's default link color.
      @include themed() {
        color: t($tertiary);
      }
    }
  }

  // BaseButton vertically centers itself with auto margins, which would
  // consume the free space and push the message to the top.
  .base-button {
    margin-top: 0;
    margin-bottom: 0;
  }
}
</style>
